import { useEffect, useRef, useState } from "react";
import {
  documentsymbolgreen,
  greyuploadicon,
  uploadbigicon,
} from "../../landingscreen/patientcharts/tabs/Document/svgs";
import { Backdrop, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import ToastMessage from "../../utils/mui/righttoptoast";
import { faxDocumentFile } from "../faxApi";
import { faxAiAthenaClass, faxAiMatchHint } from "../faxAi";
import { useFaxAiSuggestedPatient } from "../faxAiParts";
import {
  ATHENA_DOC_CLASS,
  CLINICAL_SUB_TYPES,
  clinicalSubTypeLabel,
  defaultAthenaCategory,
  faxRefOf,
  fileFaxInAthena,
  pickOf,
  sameFax,
} from "../athenaFiling";
import { searchicongrey } from "../../colorcoding/svg/iconsvg";
import api from "../../api/api";
import { athenaErrorMessage } from "../../api/athenaConfirm";
import careiqapi from "../../api/careiqapi";
import { useSelector } from "react-redux";
import { getPracticeId } from "../../config/practice";

/*
 * Send to EMR (Communication > Fax, 9928): the fax (plus any files staff add)
 * goes onto the patient's care timeline as a local document, and a Tasks
 * review task is raised. No Athena call.
 *
 * "Send to Athena" box (owner 10-07; unchecked by default, needs the Tasks
 * privilege "send to athena"): ticked, each file is ALSO filed in athenaNet
 * through the same route, FormData and duplicate guard as the viewer's own
 * Send to Athena (../athenaFiling.js), then the timeline copy is linked to that
 * Athena copy (v2 .../documents/local/{docid}/link-athena: no Athena call), so
 * the timeline shows one card and "Sync to Athena" never files it twice.
 * Encounter has no Athena class here: the box is off for it.
 *
 * Per file, remembered while the modal is open: an Athena failure keeps the
 * modal up and Upload again retries Athena only (no second timeline copy, no
 * second review task). The patient and the type are pre-selected from the
 * fax's AI read, as in Send to Athena; staff change them before Upload.
 * faxtable.jsx mounts a fresh modal for each open.
 */

/* ------------------ CONSTANTS ------------------ */
const DOCUMENT_TYPES = ["Imaging", "Labs", "Clinical", "Encounter"];

const subviews = {
  Imaging: 1,
  Labs: 0,
  Clinical: 3,
  Encounter: 2,
};

const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

// Care timeline categories (Labs/Clinical are clinical documents; the "[Labs]"
// tag in the description keeps them recognizable).
const CARE_DOCTYPE = {
  Imaging: "imagingresult",
  Labs: "clinicaldocument",
  Clinical: "clinicaldocument",
  Encounter: "encounterdocument",
};

// The Athena class a document type files as (same category as its timeline
// copy, so the two merge into one card). Encounter: none.
const ATHENA_CATEGORY = { Imaging: "IMAGING", Labs: "CLINICAL", Clinical: "CLINICAL" };

// The type the modal opens on: the AI label's class, else the practice default.
const typeOfCategory = (category) => (category === "CLINICAL" ? "Clinical" : "Imaging");

const ATHENA_HELP = "Off: saved to the patient's timeline in care only. On: also filed in athenaNet.";
const NO_PRIVILEGE_TITLE = "Filing in Athena needs the \"Send to Athena\" permission.";
const ENCOUNTER_TITLE = "Encounter documents are saved to the timeline only.";

const generateFileName = (category, file) => `${category}_${dayjs().valueOf()}_${file.name}`;

// The fax PDF from the v2 document route with the staff token (faxApi.js), not
// the hub's viewdoc and the dallas proxy.
const fetchFaxPdfAsFile = (faxItem) =>
  faxDocumentFile(faxItem, `Fax_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);

/* ------------------ COMPONENT ------------------ */
const SendToEmrDocument = ({ open, setsubview, selectedFax, aiRow, canSendToAthena = false, onClose, onFiled }) => {
  const practiseid = getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);

  // The fax's AI read: only from aiRow when it is this fax (never another fax's).
  const faxRow = open && selectedFax ? (sameFax(aiRow, selectedFax) ? aiRow : selectedFax) : null;
  const classFromFax = faxAiAthenaClass(faxRow);
  const patientFromFax = useFaxAiSuggestedPatient(faxRow);
  const pickFromFax = pickOf(patientFromFax);
  // Set once staff edit the field / type: the AI read never overrides them.
  const patientTouchedRef = useRef(false);
  const typeTouchedRef = useRef(false);

  const [files, setFiles] = useState([]);
  const [selectedType, setSelectedType] = useState(() =>
    typeOfCategory(classFromFax?.category || defaultAthenaCategory())
  );
  const [customType, setCustomType] = useState("");
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(() => pickFromFax);
  // The chart pre-selected from the fax (null once staff pick or type).
  const [prefilledPatientId, setPrefilledPatientId] = useState(() =>
    pickFromFax ? String(pickFromFax.patientid) : null
  );
  const [patientSearch, setPatientSearch] = useState(() => pickFromFax?.patientinfo || "");
  const [documentSearch, setDocumentSearch] = useState("");
  const [sendAthena, setSendAthena] = useState(false);
  const [clinicalSubType, setClinicalSubType] = useState(() => classFromFax?.subclass || "CLINICALDOCUMENT");
  const [internalNote, setInternalNote] = useState("");
  const searchTimeoutRef = useRef(null);
  const latestSearchRef = useRef("");
  // "<patient>|<care doctype>|<file>" -> { docId, isNew, inAthena, athena, linked }
  const progressRef = useRef(new Map());
  // "<patient>|<care doctype>": the review task was raised
  const taskDoneRef = useRef(new Set());
  const faxFileRef = useRef(null);
  // One send at a time: a second click before the first render disables Upload is ignored.
  const sendingRef = useRef(false);

  // The chart can arrive after the modal opened (v2 lists ids only; the name
  // comes from GET fax/{id}/ai): until staff touch the field, it follows it.
  const pickKey = pickFromFax ? `${pickFromFax.patientid}:${pickFromFax.patientinfo}:${pickFromFax.dob}` : "";
  useEffect(() => {
    if (patientTouchedRef.current) return;
    setSelectedSuggestion(pickFromFax);
    setPatientSearch(pickFromFax ? pickFromFax.patientinfo : "");
    setPrefilledPatientId(pickFromFax ? String(pickFromFax.patientid) : null);
    // pickKey says when the suggestion changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickKey]);

  // Likewise the type, when the label arrives while the modal is open.
  const classKey = classFromFax ? `${classFromFax.category}:${classFromFax.subclass || ""}` : "";
  useEffect(() => {
    if (!classFromFax || typeTouchedRef.current) return;
    setSelectedType(typeOfCategory(classFromFax.category));
    if (classFromFax.subclass) setClinicalSubType(classFromFax.subclass);
    // classKey says when the class changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classKey]);

  if (!open) return null;

  /* ------------------ DERIVED ------------------ */
  const isPatientSelected = Boolean(selectedSuggestion);
  const hasFax = Boolean(selectedFax);
  const athenaCategory = ATHENA_CATEGORY[selectedType] || null;
  const athenaBoxDisabled = !canSendToAthena || !athenaCategory || !isPatientSelected;
  const athenaOn = canSendToAthena && sendAthena && Boolean(athenaCategory);
  const athenaBoxTitle = !canSendToAthena ? NO_PRIVILEGE_TITLE : !athenaCategory ? ENCOUNTER_TITLE : undefined;
  const patientIsFromFax =
    isPatientSelected && prefilledPatientId !== null && String(selectedSuggestion.patientid) === prefilledPatientId;
  const patientHint = patientIsFromFax ? faxAiMatchHint(patientFromFax?.confidence) : "";
  const classHint =
    classFromFax &&
    classFromFax.label &&
    athenaCategory === classFromFax.category &&
    (athenaCategory !== "CLINICAL" || clinicalSubType === classFromFax.subclass)
      ? classFromFax.label
      : "";
  const isUploadDisabled = (!files.length && !hasFax) || !selectedType || !isPatientSelected || loading;

  /* ------------------ HELPERS ------------------ */
  const handleToastClose = () => setOpenToast(false);

  const toast = (message, severity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  const resetForm = () => {
    setFiles([]);
    setSelectedType(typeOfCategory(defaultAthenaCategory()));
    setCustomType("");
    setToastMessage("");
    setSuggestions([]);
    setShowSuggestions(false);
    setDocumentSearch("");
    setPatientSearch("");
    setSelectedSuggestion(null);
    setPrefilledPatientId(null);
    patientTouchedRef.current = true;
    typeTouchedRef.current = true;
    setSendAthena(false);
    setClinicalSubType("CLINICALDOCUMENT");
    setInternalNote("");
    progressRef.current = new Map();
    taskDoneRef.current = new Set();
    faxFileRef.current = null;
    setOpenToast(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const pickType = (type) => {
    typeTouchedRef.current = true;
    setSelectedType(type);
    // A type with no Athena class (Encounter) unticks the box: picking Imaging again must
    // not bring back a tick the person can no longer see was set (review 10-07).
    if (!ATHENA_CATEGORY[type]) setSendAthena(false);
  };

  const handleDocumentSearch = (value) => {
    setDocumentSearch(value);
    const match = DOCUMENT_TYPES.find((t) => t.toLowerCase() === value.toLowerCase());
    if (match) pickType(match);
  };

  /* ------------------ SEARCH ------------------ */
  const handlePatientSearch = (value) => {
    patientTouchedRef.current = true;
    setPrefilledPatientId(null);
    setPatientSearch(value);
    setSelectedSuggestion(null);
    latestSearchRef.current = value;

    // Clear previous debounce
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce API call
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.post(`/v1/${practiseid}/autocomplete/dssearch`, {
          searchtxt: value,
          // Fax filing is practice-wide front-office work — search across
          // departments (QA 2026-08-31: Frisco staff couldn't find patients).
          alldepts: true,
        });

        // Ignore stale responses
        if (latestSearchRef.current !== value) return;

        if (res.status === 200 && Array.isArray(res.data)) {
          setSuggestions(res.data.slice(0, 3));
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Search error:", error);
      }
    }, 300);
  };

  const handleSuggestionSelect = (item) => {
    patientTouchedRef.current = true;
    setPrefilledPatientId(null);
    setPatientSearch(item.patientinfo || "");
    setSelectedSuggestion(item);
    setShowSuggestions(false);
  };

  /* ------------------ FILE HANDLER ------------------ */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const validFiles = [];

    for (const file of selectedFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast("Only PDF, PNG, JPG files are allowed", "error");
        continue;
      }

      if (file.size > 25 * 1024 * 1024) {
        toast("Each file must be under 25MB", "error");
        continue;
      }

      validFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* ------------------ SEND STEPS ------------------ */

  // 1. The care timeline copy: { docId, isNew, inAthena }. A file the chart already
  //    has (the gateway asked, ../../api/athenaConfirm.js, and the person chose not to
  //    upload it again) answers with that copy.
  const saveToTimeline = async (file, targetPid, doctype, dept) => {
    const fd = new FormData();
    fd.append("file", file, generateFileName(selectedType, file));
    fd.append("doctype", doctype);
    // Raw fax filenames (fax_7266336_fax.pdf) made useless timeline labels
    // (QA 2026-08-27) — fall back to a readable type+date name when the biller
    // doesn't type one.
    const prettyName =
      customType ||
      (/^fax[_-]?\d*/i.test(file.name) ? `Faxed ${selectedType} ${dayjs().format("MM/DD/YYYY")}` : file.name);
    fd.append("description", `${prettyName} [${selectedType}, Fax]`);
    fd.append("departmentid", dept);
    fd.append("uploaded_by", userConfig?.username || "");
    try {
      // careiqapi's interceptor returns the body directly.
      const upRes = await careiqapi.post(
        `/v1/fhir-mirror/${practiseid}/patient/${targetPid}/documents/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (upRes?.duplicate_prevented) {
        // a gateway that answered with the copy already there (no dialog)
        return { docId: upRes.documentid || upRes.duplicate_of || null, isNew: false, inAthena: !!upRes.synced };
      }
      return { docId: upRes?.documentid || upRes?.data?.documentid || null, isNew: true, inAthena: false };
    } catch (dupErr) {
      const d = dupErr?.response?.data;
      if (dupErr?.response?.status === 409 && d?.code === "duplicate_upload") {
        return { docId: d.duplicate_of || null, isNew: false, inAthena: !!d.synced };
      }
      throw dupErr;
    }
  };

  // 2. Athena (box ticked): "filed" | "already" (that class has it; the person did not
  //    want a second copy) | "declined" (filed as the other class, or an earlier send
  //    got no answer, and the person did not want it sent). Anything else throws.
  const fileInAthena = async (file, targetPid, isFax) => {
    let res;
    try {
      res = await fileFaxInAthena({
        practiceId: practiseid,
        patientId: targetPid,
        file,
        category: athenaCategory,
        subclass: clinicalSubType,
        internalNote,
        faxRef: isFax ? faxRefOf(selectedFax) : null,
        departmentId: userConfig?.config?.departmentid,
      });
    } catch (err) {
      const d = err?.response?.status === 409 ? err.response.data : null;
      if (d?.code === "already_filed") return d.filed_as === ATHENA_DOC_CLASS[athenaCategory] ? "already" : "declined";
      if (d?.code === "athena_unknown") return "declined";
      throw err;
    }
    if (res?.code === "athena_busy") {
      // another send of this file is in flight: not done yet
      throw Object.assign(new Error("busy"), { response: { data: res } });
    }
    return res?.duplicate_prevented ? "already" : "filed";
  };

  // 3. One card on the timeline: the local copy is marked as that Athena copy
  //    (the gateway reads the Athena id from its own filing record).
  const linkTimelineCopy = (targetPid, docId) =>
    careiqapi.post(`/v1/fhir-mirror/${practiseid}/patient/${targetPid}/documents/local/${docId}/link-athena`);

  // 4. Task for the doctor to review (shows in the dashboard Tasks list).
  const createReviewTask = async (targetPid, doctype, dept, docId) => {
    try {
      const providerName =
        `${userConfig?.firstname || ""} ${userConfig?.lastname || ""}`.trim() || userConfig?.username || "";
      await api.post(`/v1/${practiseid}/patient/${targetPid}/orders/create`, {
        department_id: Number(dept) || 1,
        patient_name: selectedSuggestion?.patientinfo?.split(" - ")[0] || "",
        tov: "FOLLOW UP",
        lov: 0,
        diagnosis: "",
        ordertype: "Task",
        ordername: `Review ${selectedType.toLowerCase()} (faxed) — from chart timeline`,
        schedule: "f",
        provider: providerName,
        user_role: "ALL",
        status: "CREATED",
        confirm_status: "C",
        orderdate: dayjs().format("YYYY-MM-DD"),
        encounterid: 0,
        // Lets the Tasks page open the uploaded doc inline (2026-08-21).
        ...(docId ? { result: `docref:${docId}:${targetPid}:${doctype}` } : {}),
      });
    } catch (taskErr) {
      // The upload succeeded; only the task failed.
      console.error("review-task create failed", taskErr);
    }
  };

  const closeSoon = () => {
    setTimeout(() => {
      setLoading(false);
      resetForm();
      onClose();
      if (setsubview) setsubview(subviews[selectedType]);
    }, 1200);
  };

  const submit = async () => {
    if (isUploadDisabled || sendingRef.current) return;
    sendingRef.current = true;
    const targetPid = selectedSuggestion.patientid;
    const doctype = CARE_DOCTYPE[selectedType] || "clinicaldocument";
    const dept = String(userConfig?.config?.departmentid || "1");
    const toAthena = athenaOn;
    try {
      setLoading(true);

      // the files staff added, then the fax
      const queue = files.map((file) => ({
        file,
        key: `file:${file.name}:${file.size}:${file.lastModified}`,
        isFax: false,
      }));
      if (selectedFax) {
        if (!faxFileRef.current) faxFileRef.current = await fetchFaxPdfAsFile(selectedFax);
        queue.push({ file: faxFileRef.current, key: `fax:${faxRefOf(selectedFax) || ""}`, isFax: true });
      }

      const results = [];
      let athenaError = "";
      let linkFailed = 0;
      for (const item of queue) {
        const pkey = `${targetPid}|${doctype}|${item.key}`;
        let st = progressRef.current.get(pkey);
        if (!st) {
          st = { ...(await saveToTimeline(item.file, targetPid, doctype, dept)), athena: null, linked: false };
          progressRef.current.set(pkey, st);
        }
        results.push(st);
        if (!toAthena) continue;
        if (!st.athena) {
          if (st.inAthena) {
            // the timeline copy the chart already had was synced to Athena already
            st.athena = "already";
          } else {
            try {
              st.athena = await fileInAthena(item.file, targetPid, item.isFax);
            } catch (err) {
              console.error(err);
              if (!athenaError) athenaError = athenaErrorMessage(err, "Athena did not accept it.");
              continue;
            }
          }
        }
        if (st.athena !== "declined" && !st.inAthena && st.docId && !st.linked) {
          try {
            await linkTimelineCopy(targetPid, st.docId);
            st.linked = true;
          } catch (linkErr) {
            console.warn("timeline copy not linked to its Athena copy", linkErr);
            linkFailed += 1;
          }
        }
      }

      const anyNew = results.some((r) => r.isNew);
      if (!toAthena && !anyNew) {
        // nothing new: the first upload already raised the review task
        toast("Already on this patient's timeline - not uploaded again", "info");
        setLoading(false);
        sendingRef.current = false;
        return;
      }

      // One review task per chart and type, even when Upload is pressed again to retry Athena.
      const taskKey = `${targetPid}|${doctype}`;
      if (anyNew && !taskDoneRef.current.has(taskKey)) {
        taskDoneRef.current.add(taskKey);
        await createReviewTask(targetPid, doctype, dept, results.find((r) => r.isNew && r.docId)?.docId);
      }

      const where = anyNew ? "Saved to timeline" : "Already on the timeline";
      // The fax's work log (faxtable.jsx, owner 10-11): filed to this chart; once per send.
      const filedInAthena = toAthena && results.some((r) => r.athena === "filed" || r.athena === "already");
      if (selectedFax && typeof onFiled === "function" && (anyNew || filedInAthena)) {
        onFiled({ action: "filed_emr", patientId: targetPid, athena: filedInAthena });
      }
      if (!toAthena) {
        toast(where, "success");
        closeSoon();
        return;
      }
      if (athenaError) {
        // the timeline copy stays; Upload again files the rest in Athena only
        toast(`${where}, but not filed in Athena: ${athenaError} Press Upload Document to try Athena again.`, "error");
        setLoading(false);
        sendingRef.current = false;
        return;
      }
      const outcomes = results.map((r) => r.athena);
      let message = `${where}; not filed in Athena`;
      let severity = "info";
      if (outcomes.includes("filed")) {
        message = `${where} and filed in Athena`;
        severity = "success";
      } else if (outcomes.includes("already")) {
        message = `${where}; already in Athena (not filed again)`;
      }
      if (linkFailed) {
        message += ". The timeline copy is not linked to the Athena copy yet, so it may show twice";
        severity = "warning";
      }
      toast(message, severity);
      closeSoon();
    } catch (error) {
      console.error(error);
      setLoading(false);
      sendingRef.current = false;
      toast("Failed to upload documents", "error");
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40">
        <div className="flex min-h-screen items-start justify-center py-10">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg" data-testid="send-to-emr">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div className="text-lg font-semibold flex items-center gap-2">
                <span>{greyuploadicon}</span>
                Send to EMR
              </div>
              <button onClick={handleClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="flex justify-center mt-2">
              <div className="relative w-full mx-4">
                <div
                  className={`flex items-center gap-2 px-4 h-[40px]
      rounded-[19px] border bg-[#F5F6FA]
      ${selectedSuggestion ? "border-blue-500" : "border-[#D5D5D5]"}`}
                >
                  <div>{searchicongrey}</div>

                  <input
                    type="text"
                    placeholder="Search Patient"
                    aria-label="Patient"
                    value={selectedSuggestion?.patientinfo ? selectedSuggestion.patientinfo : patientSearch}
                    onChange={(e) => handlePatientSearch(e.target.value)}
                    // a suggested chart is selected whole, so typing replaces it
                    onFocus={(e) => {
                      if (patientIsFromFax) e.target.select();
                    }}
                    className="flex-1 bg-transparent outline-none text-sm"
                  />
                </div>

                {patientIsFromFax && (
                  <div className="mt-1 px-2 text-start text-xs text-[#7E7E7E]" data-testid="emr-patient-from-fax">
                    Suggested from the fax — change if wrong
                    {selectedSuggestion.dob ? ` · DOB ${selectedSuggestion.dob}` : ""}
                    {patientHint ? ` (${patientHint})` : ""}
                  </div>
                )}

                {/* Suggestions */}
                {showSuggestions && suggestions.length > 0 && patientSearch && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow">
                    {suggestions.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionSelect(item)}
                        className="cursor-pointer px-4 py-2 text-sm text-start hover:bg-gray-100"
                      >
                        {item?.patientinfo}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-5 py-4">
              <label
                className={`flex flex-col items-center justify-center rounded-lg border-[2px] p-6 text-center
    ${isPatientSelected ? "cursor-pointer border-[#E5E7EB]" : "cursor-not-allowed border-gray-300 opacity-50"}`}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  disabled={!isPatientSelected}
                  onChange={handleFileChange}
                />

                {!files.length ? (
                  <>
                    <div className="mb-2 bg-[#F3F4F6] rounded-full h-12 w-12 flex items-center justify-center">
                      {uploadbigicon}
                    </div>
                    <p className="text-sm font-medium">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500">PDF, PNG, JPG up to 25MB</p>
                  </>
                ) : (
                  <>
                    <div className="mb-2 bg-[#D0FAE5] rounded-full h-10 w-10 flex items-center justify-center">
                      {documentsymbolgreen}
                    </div>
                    <div className="mt-1 w-full max-h-40 overflow-y-auto pr-1">
                      {files.map((file, index) => (
                        <div key={index} className="mb-2 flex items-center justify-between rounded-md border p-2 text-left">
                          <div className="truncate">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="ml-2 text-xs text-red-500 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </label>

              {/* Document Type */}
              <p className="mt-4 mb-2 text-sm font-semibold text-start">Document Type</p>

              <input
                type="text"
                placeholder="Search"
                value={documentSearch}
                onChange={(e) => handleDocumentSearch(e.target.value)}
                className={`w-full mb-3 rounded-full px-4 py-2 text-sm border
    ${!isPatientSelected ? "bg-gray-100 cursor-not-allowed" : "bg-[#F5F6FA] border-[#D5D5D5]"}`}
              />

              <div className="mb-3 grid grid-cols-2 gap-2">
                {DOCUMENT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={selectedType === type}
                    disabled={!isPatientSelected}
                    onClick={() => pickType(type)}
                    className={`rounded-lg border px-3 py-2 text-sm
      ${!isPatientSelected
        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
        : selectedType === type
          ? "bg-blue-50 border-blue-500 text-blue-600"
          : "border-gray-300"}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              {classHint && (
                <div className="-mt-1 mb-3 text-start text-xs text-[#7E7E7E]" data-testid="emr-class-from-fax">
                  From the AI label: {classHint}
                </div>
              )}

              <input
                type="text"
                placeholder="Specify document type"
                value={customType}
                disabled={!isPatientSelected}
                onChange={(e) => setCustomType(e.target.value)}
                className={`w-full rounded-xl border px-3 py-2 text-sm
    ${!isPatientSelected ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />

              {/* Send to Athena (unchecked: timeline only) */}
              <div className="mt-4 border-t pt-3 text-start" data-testid="emr-athena">
                <label
                  title={athenaBoxTitle}
                  className={`inline-flex items-center gap-2 text-sm font-semibold ${
                    athenaBoxDisabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer text-gray-800"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={athenaOn}
                    disabled={athenaBoxDisabled}
                    onChange={(e) => setSendAthena(e.target.checked)}
                    aria-describedby="emr-athena-help"
                    className="h-4 w-4"
                  />
                  Send to Athena
                </label>
                <p id="emr-athena-help" className="mt-1 text-xs text-[#7E7E7E]">
                  {ATHENA_HELP}
                </p>
                {canSendToAthena && !athenaCategory && (
                  <p className="mt-1 text-xs text-[#7E7E7E]">{ENCOUNTER_TITLE}</p>
                )}

                {athenaOn && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <span className="text-sm font-semibold">Category</span>
                      <div className="mt-2 flex gap-2">
                        {["IMAGING", "CLINICAL"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            aria-pressed={athenaCategory === t}
                            onClick={() => {
                              // keeps the timeline copy in the same category (one card)
                              if (t === "IMAGING") pickType("Imaging");
                              else if (athenaCategory !== "CLINICAL") pickType("Clinical");
                            }}
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                              athenaCategory === t
                                ? "border-gray-800 bg-gray-100 text-gray-800"
                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    {athenaCategory === "CLINICAL" && (
                      <div>
                        <label className="text-sm font-semibold" htmlFor="emr-athena-subclass">
                          Clinical Document Type
                        </label>
                        <select
                          id="emr-athena-subclass"
                          value={clinicalSubType}
                          onChange={(e) => {
                            typeTouchedRef.current = true;
                            setClinicalSubType(e.target.value);
                          }}
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                        >
                          {CLINICAL_SUB_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {clinicalSubTypeLabel(t)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-semibold" htmlFor="emr-athena-note">
                        Internal Note
                      </label>
                      <textarea
                        id="emr-athena-note"
                        rows={2}
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        placeholder="Add internal note (optional)"
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t px-5 py-3">
              <button onClick={handleClose} className="rounded-md border px-4 py-2 text-sm">
                Cancel
              </button>

              <button
                disabled={isUploadDisabled}
                onClick={submit}
                className={`rounded-md px-4 py-2 text-sm text-white ${
                  isUploadDisabled ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Upload Document
              </button>
            </div>
          </div>
        </div>

        <ToastMessage message={toastMessage} severity={toastSeverity} open={openToast} handleClose={handleToastClose} />
      </div>
    </>
  );
};

export default SendToEmrDocument;
